"use client";

import { type ReactNode, useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Wordy phrase → concise replacement map (module level)             */
/* ------------------------------------------------------------------ */

const WORDY_PHRASES: Record<string, string> = {
  "due to the fact that": "because",
  "owing to the fact that": "because",
  "in view of the fact that": "because",
  "in spite of the fact that": "although",
  "despite the fact that": "although",
  "in the event that": "if",
  "at this point in time": "now",
  "at that point in time": "then",
  "at the present time": "now",
  "has the ability to": "can",
  "is able to": "can",
  "in order to": "to",
  "for the purpose of": "for",
  "a large number of": "many",
  "a sufficient number of": "enough",
  "a small number of": "a few",
  "the majority of": "most",
  "a majority of": "most",
  "at all times": "always",
  "on a daily basis": "daily",
  "on a regular basis": "regularly",
  "in the near future": "soon",
  "until such time as": "until",
  "by means of": "by",
  "in close proximity to": "near",
  "in the vicinity of": "near",
  "in the absence of": "without",
  "with regard to": "about",
  "with the exception of": "except for",
  "in conjunction with": "with",
  "as a result of": "because of",
  "during the course of": "during",
  "prior to": "before",
  "subsequent to": "after",
  "give consideration to": "consider",
  "take into consideration": "consider",
  "make a decision": "decide",
  "make an assumption": "assume",
  "came to the conclusion that": "concluded that",
  "conduct an investigation of": "investigate",
  "it is important to note that": "note that",
  "it is possible that": "perhaps",
  "there is no doubt that": "undoubtedly",
  "each and every": "every",
  "first and foremost": "first",
  "in a timely manner": "promptly",
  "whether or not": "whether",
};

const WORDY_KEYS = Object.keys(WORDY_PHRASES).sort(
  (a, b) => b.length - a.length
);
const RE_WORDY = new RegExp(`\\b(?:${WORDY_KEYS.join("|")})\\b`, "gi");
const RE_SPLIT_WORDS = /\s+/;

/** Phrases that end in "of" and shrink to a bare quantifier ("most", "many"...). */
const QUANTIFIER_OF_PHRASES = new Set([
  "a large number of",
  "a sufficient number of",
  "a small number of",
  "the majority of",
  "a majority of",
]);
/** A following word that needs "of" kept: "most of the X", "most of them". */
const RE_NEEDS_OF =
  /^\s+(?:the|these|those|this|that|my|your|his|her|its|our|their|them|us|you|it|him|me)\b/i;

/* ------------------------------------------------------------------ */
/*  Analysis                                                          */
/* ------------------------------------------------------------------ */

export interface WordyFinding {
  index: number;
  length: number;
  matched: string;
  replacement: string;
  savedWords: number;
}

interface AnalysisResult {
  wordCount: number;
  findings: WordyFinding[];
  totalSavedWords: number;
  highlighted: ReactNode[];
}

function countWords(value: string): number {
  return value.split(RE_SPLIT_WORDS).filter(Boolean).length;
}

function matchCase(matched: string, replacement: string): string {
  if (matched.length > 0 && matched[0] === matched[0].toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

function conciseFor(phrase: string, after: string): string | undefined {
  const key = phrase.toLowerCase();
  const concise = WORDY_PHRASES[key];
  if (concise && QUANTIFIER_OF_PHRASES.has(key) && RE_NEEDS_OF.test(after)) {
    return `${concise} of`;
  }
  return concise;
}

export function collectFindings(text: string): WordyFinding[] {
  const findings: WordyFinding[] = [];
  for (const match of text.matchAll(RE_WORDY)) {
    const concise = conciseFor(
      match[0],
      text.slice(match.index + match[0].length)
    );
    if (!concise) {
      continue;
    }
    findings.push({
      index: match.index,
      length: match[0].length,
      matched: match[0],
      replacement: matchCase(match[0], concise),
      savedWords: Math.max(countWords(match[0]) - countWords(concise), 0),
    });
  }
  return findings;
}

function buildHighlighted(text: string, findings: WordyFinding[]): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const finding of findings) {
    if (finding.index > last) {
      nodes.push(text.slice(last, finding.index));
    }
    nodes.push(
      <mark
        className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-900/60 dark:text-yellow-100"
        key={`wordy-${finding.index}`}
      >
        {finding.matched}
      </mark>
    );
    last = finding.index + finding.length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

function analyzeWordiness(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const findings = collectFindings(text);
  const totalSavedWords = findings.reduce((sum, f) => sum + f.savedWords, 0);

  return {
    wordCount: countWords(trimmed),
    findings,
    totalSavedWords,
    highlighted: buildHighlighted(text, findings),
  };
}

function applyFinding(text: string, finding: WordyFinding): string {
  return (
    text.slice(0, finding.index) +
    finding.replacement +
    text.slice(finding.index + finding.length)
  );
}

export function applyAllFindings(
  text: string,
  findings: WordyFinding[]
): string {
  let result = text;
  const descending = findings.slice().sort((a, b) => b.index - a.index);
  for (const finding of descending) {
    result = applyFinding(result, finding);
  }
  return result;
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Widget                                                            */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const WORDINESS_CHECKER_STRINGS = {
  savesWords: "one: (saves {count} word)|other: (saves {count} words)",
  textAriaLabel: "Text to check for wordiness",
  inputPlaceholder: "Paste or type your text here to check for wordiness...",
  totalWords: "Total Words",
  wordyPhrases: "Wordy Phrases",
  potentialWordSavings: "Potential Word Savings",
  highlightedHeading: "Your Text with Wordy Phrases Highlighted",
  suggestedFixes: "Suggested Fixes",
  applyAll: "Apply All ({count})",
  apply: "Apply",
  noneFoundTitle: "No wordy phrases detected!",
  noneFoundBody:
    "None of the common wordy phrases on our list appear in your text. Concise writing!",
} as const;

export default function WordinessCheckerWidget() {
  const strings = useToolUi(WORDINESS_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [text, setText] = useState("");

  const analysis = useMemo(() => analyzeWordiness(text), [text]);

  function handleApply(finding: WordyFinding) {
    setText(applyFinding(text, finding));
  }

  function handleApplyAll() {
    if (analysis && analysis.findings.length > 0) {
      setText(applyAllFindings(text, analysis.findings));
    }
  }

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.inputPlaceholder}
        value={text}
      />

      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.wordyPhrases}
            value={fmt(analysis.findings.length)}
          />
          <StatCard
            label={strings.potentialWordSavings}
            value={fmt(analysis.totalSavedWords)}
          />
        </div>
      )}

      {/* Highlighted text */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.highlightedHeading}
          </h2>
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {analysis.highlighted}
          </div>
        </section>
      )}

      {/* Findings + apply */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.suggestedFixes}
            </h2>
            <button
              className="rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
              onClick={handleApplyAll}
              type="button"
            >
              {strings.applyAll.replace(
                "{count}",
                String(analysis.findings.length)
              )}
            </button>
          </div>
          <div className="space-y-3">
            {analysis.findings.map((finding) => (
              <div
                className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4"
                key={`${finding.index}-${finding.matched}`}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  <span className="font-medium text-red-600 dark:text-red-400">
                    {finding.matched}
                  </span>
                  <span className="text-muted-foreground"> → </span>
                  <span className="font-medium text-green-700 dark:text-green-400">
                    {finding.replacement}
                  </span>
                  {finding.savedWords > 0 && (
                    <span className="text-muted-foreground text-xs">
                      {" "}
                      {tPlural(strings.savesWords, locale, finding.savedWords)}
                    </span>
                  )}
                </p>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                  onClick={() => handleApply(finding)}
                  type="button"
                >
                  {strings.apply}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* None found */}
      {analysis && analysis.findings.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noneFoundTitle}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.noneFoundBody}
          </p>
        </div>
      )}
    </>
  );
}
